import { useRouter } from 'next/router';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce, useLocalstorageState } from 'rooks';
import shallow from 'zustand/shallow';

import { useTalkPageContext } from 'components/pages/talks/slug/talk-page-context';
import useVideoPlayerStore from 'components/video-player/store';
import { useIsBreakpointWidth } from 'lib/hooks/useIsBreakpointWidth';

// Types
export interface SubtitlePreference {
  isEnabled: boolean;
  language: string;
}

export interface SubtitlesContextType {
  subtitle: string;
  handleSubtitle: (code: string) => void;
  previouslySelectedSubtitle: string;
  setPreviouslySelectedSubtitle: (code: string) => void;
  showAllLanguages: boolean;
  setShowAllLanguages: (x: boolean) => void;
  isMobile: boolean;
  subtitlePreference: SubtitlePreference;
}

// Context Creation
export const SubtitlesContext = createContext<SubtitlesContextType | null>(
  null
);

const SubtitlesProvider = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const { language, subtitle: querySubtitle } = useTalkPageContext();

  const {
    audio,
    isReady,
    requestSubtitleLanguage,
    onSubtitleSet,
    onTranscriptLanguage,
    languages
  } = useVideoPlayerStore(
    state => ({
      audio: state.audio,
      isReady: state.ready,
      subtitle: state.subtitle,
      requestSubtitleLanguage: state.requestSubtitleLanguage,
      onSubtitleSet: state.onSubtitleSet,
      onTranscriptLanguage: state.onTranscriptLanguage,
      languages: state.languages
    }),
    shallow
  );

  // Local State
  const [subtitle, setSubtitle] = useState<string>('');
  const [previouslySelectedSubtitle, setPreviouslySelectedSubtitle] =
    useState(language);
  const [showAllLanguages, setShowAllLanguages] = useState(true);
  const isMobile = useIsBreakpointWidth({ size: 'md' });

  // Persistent State
  const [subtitlePreference, setSubtitlePreference] =
    useLocalstorageState<SubtitlePreference>('subtitle_preference', {
      isEnabled: false,
      language: 'en'
    });

  // Add a ref to track the last set subtitle
  const lastSetSubtitle = useRef<string>('');
  const isInitialMount = useRef(true);

  // Create a debounced version of onSubtitleSet
  const debouncedOnSubtitleSet = useDebounce(onSubtitleSet, 300);

  // Helper Functions
  const isLanguageAvailable = useCallback(
    (langCode: string) =>
      languages?.some(lang => lang.languageCode === langCode),
    [languages]
  );

  // URL Management
  const updateQueryParams = useCallback(
    (queries: typeof router.query) => {
      router.replace(
        {
          pathname: router.pathname,
          query: queries
        },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );

  // Helper function to handle subtitle setting
  const handleSubtitleSet = useCallback(
    (newSubtitle: string) => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }

      if (lastSetSubtitle.current !== newSubtitle) {
        lastSetSubtitle.current = newSubtitle;
        debouncedOnSubtitleSet();
      }
    },
    [debouncedOnSubtitleSet]
  );

  // Subtitle State Management
  useEffect(() => {
    if (!isReady || !languages) return;

    let effectiveSubtitle = querySubtitle as string;
    let shouldUpdateQuery = false;
    let shouldRemoveQuery = false;

    // Handle disabled subtitles
    if (!subtitlePreference.isEnabled) {
      effectiveSubtitle = 'OFF';
      shouldRemoveQuery = true;
    }
    // Handle enabled subtitles with no current subtitle
    else if (!effectiveSubtitle && subtitlePreference.isEnabled) {
      if (isLanguageAvailable(subtitlePreference.language)) {
        effectiveSubtitle = subtitlePreference.language;
        shouldUpdateQuery = true;
      } else if (isLanguageAvailable('en')) {
        effectiveSubtitle = 'en';
        shouldUpdateQuery = true;
        setSubtitlePreference({
          isEnabled: true,
          language: 'en'
        });
      } else {
        effectiveSubtitle = 'OFF';
        setSubtitlePreference({
          isEnabled: false,
          language: subtitlePreference.language
        });
        shouldRemoveQuery = true;
      }
    }

    // Handle muted state default
    if (!effectiveSubtitle && audio.mute && isLanguageAvailable('en')) {
      effectiveSubtitle = 'en';
      shouldUpdateQuery = true;
    }

    effectiveSubtitle = effectiveSubtitle || 'OFF';

    // Update URL parameters
    const queries = { ...router.query };
    if (shouldRemoveQuery) {
      delete queries.subtitle;
      delete queries.language;
      updateQueryParams(queries);
    } else if (shouldUpdateQuery && effectiveSubtitle !== 'OFF') {
      updateQueryParams({ ...queries, subtitle: effectiveSubtitle });
    }

    // Update subtitle state and trigger events
    setSubtitle(effectiveSubtitle);
    if (effectiveSubtitle !== 'OFF') {
      setPreviouslySelectedSubtitle(effectiveSubtitle);
      requestSubtitleLanguage(effectiveSubtitle);
      handleSubtitleSet(effectiveSubtitle);
      onTranscriptLanguage(effectiveSubtitle);
    } else {
      requestSubtitleLanguage('OFF');
      lastSetSubtitle.current = 'OFF';
    }
  }, [
    isReady,
    languages,
    querySubtitle,
    subtitlePreference,
    audio.mute,
    isLanguageAvailable,
    handleSubtitleSet
  ]);

  // Subtitle Change Handler
  const handleSubtitle = useCallback(
    (languageCode: string) => {
      const queries = { ...router.query };

      if (languageCode === 'OFF') {
        delete queries.subtitle;
        delete queries.language;
        setPreviouslySelectedSubtitle(subtitle);
        requestSubtitleLanguage('OFF');
        lastSetSubtitle.current = 'OFF';
        setSubtitlePreference({
          isEnabled: false,
          language: subtitle !== 'OFF' ? subtitle : subtitlePreference.language
        });
      } else {
        const effectiveLanguage = isLanguageAvailable(languageCode)
          ? languageCode
          : isLanguageAvailable('en')
            ? 'en'
            : 'OFF';

        if (effectiveLanguage !== 'OFF') {
          setPreviouslySelectedSubtitle(effectiveLanguage);
          queries.subtitle = effectiveLanguage;
          requestSubtitleLanguage(effectiveLanguage);
          handleSubtitleSet(effectiveLanguage);
          onTranscriptLanguage(effectiveLanguage);
          setSubtitlePreference({
            isEnabled: true,
            language: effectiveLanguage
          });
        } else {
          delete queries.subtitle;
          delete queries.language;
          requestSubtitleLanguage('OFF');
          lastSetSubtitle.current = 'OFF';
          setSubtitlePreference({
            isEnabled: false,
            language: languageCode
          });
        }
      }

      updateQueryParams(queries);
      setSubtitle(languageCode);
    },
    [
      router,
      subtitle,
      subtitlePreference,
      requestSubtitleLanguage,
      onTranscriptLanguage,
      isLanguageAvailable,
      setSubtitlePreference,
      updateQueryParams,
      handleSubtitleSet
    ]
  );

  return (
    <SubtitlesContext
      value={{
        subtitle,
        handleSubtitle,
        previouslySelectedSubtitle,
        setPreviouslySelectedSubtitle,
        showAllLanguages,
        setShowAllLanguages,
        isMobile,
        subtitlePreference
      }}
    >
      {children}
    </SubtitlesContext>
  );
};

export default SubtitlesProvider;
